import type { DataProvider, Identifier } from "ra-core";

import { mockDriver } from "../../../../supabase/functions/_shared/messaging/driver.ts";
import { STUDIO_EMAIL } from "../../../../supabase/functions/_shared/vogelkop.ts";
import type { Alert, Contact, Task } from "../types";
import { isDone, isDueToday, isOverdue } from "../tasks/tasksPredicate";

export const dueRemindersWithoutAlert = <
  T extends Pick<Task, "id" | "due_date" | "done_date">,
>(
  tasks: T[],
  alerts: Pick<Alert, "kind" | "task_id">[],
): T[] => {
  const covered = new Set(
    alerts
      .filter((alert) => alert.kind === "reminder_due" && alert.task_id != null)
      .map((alert) => String(alert.task_id)),
  );
  return tasks.filter((task) => {
    if (isDone({ ...task, done_date: task.done_date ?? null })) return false;
    if (!isDueToday(task.due_date) && !isOverdue(task.due_date)) return false;
    return !covered.has(String(task.id));
  });
};

const emailAlert = async (title: string, body: string) => {
  const result = await mockDriver.send({
    channel: "email",
    to: STUDIO_EMAIL,
    from: STUDIO_EMAIL,
    subject: title,
    body,
  });
  return result.status === "failed" ? null : new Date().toISOString();
};

const alreadyAlerted = async (
  dataProvider: DataProvider,
  filter: Record<string, unknown>,
) => {
  const { data } = await dataProvider.getList<Alert>("alerts", {
    filter,
    pagination: { page: 1, perPage: 1 },
    sort: { field: "id", order: "ASC" },
  });
  return data.length > 0;
};

export const recordNewLeadAlert = async (
  dataProvider: DataProvider,
  contact: Pick<
    Contact,
    "id" | "first_name" | "last_name" | "property_address"
  >,
) => {
  if (
    await alreadyAlerted(dataProvider, {
      kind: "new_lead",
      contact_id: contact.id,
    })
  ) {
    return;
  }
  const name = `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim();
  const title = `New lead: ${name || "New lead"}`;
  const body = contact.property_address
    ? `${name} wrote about ${contact.property_address}.`
    : `${name} is a new lead.`;
  const emailedAt = await emailAlert(title, body);
  await dataProvider.create("alerts", {
    data: {
      kind: "new_lead",
      contact_id: contact.id,
      task_id: null,
      title,
      body,
      read_at: null,
      emailed_at: emailedAt,
      created_at: new Date().toISOString(),
    },
  });
};

export const recordReminderAlert = async (
  dataProvider: DataProvider,
  task: Pick<Task, "id" | "contact_id" | "text" | "due_date">,
  contact: Pick<Contact, "first_name" | "last_name"> | undefined,
) => {
  if (!isDueToday(task.due_date) && !isOverdue(task.due_date)) return;
  if (
    await alreadyAlerted(dataProvider, {
      kind: "reminder_due",
      task_id: task.id,
    })
  ) {
    return;
  }
  const name = contact
    ? `${contact.first_name} ${contact.last_name}`.trim()
    : "a lead";
  const title = `Reminder due: ${name}`;
  const body = task.text;
  const emailedAt = await emailAlert(title, body);
  await dataProvider.create("alerts", {
    data: {
      kind: "reminder_due",
      contact_id: task.contact_id,
      task_id: task.id,
      title,
      body,
      read_at: null,
      emailed_at: emailedAt,
      created_at: new Date().toISOString(),
    },
  });
};

const scanning = new Set<Identifier>();

export const scanDueReminders = async (dataProvider: DataProvider) => {
  const [{ data: tasks }, { data: alerts }, { data: contacts }] =
    await Promise.all([
      dataProvider.getList<Task>("tasks", {
        pagination: { page: 1, perPage: 200 },
        sort: { field: "due_date", order: "ASC" },
      }),
      dataProvider.getList<Alert>("alerts", {
        pagination: { page: 1, perPage: 200 },
        sort: { field: "id", order: "DESC" },
      }),
      dataProvider.getList<Contact>("contacts", {
        pagination: { page: 1, perPage: 200 },
        sort: { field: "id", order: "ASC" },
      }),
    ]);
  const missing = dueRemindersWithoutAlert(tasks, alerts);
  for (const task of missing) {
    if (scanning.has(task.id)) continue;
    scanning.add(task.id);
    try {
      const contact = contacts.find((item) => item.id === task.contact_id);
      await recordReminderAlert(dataProvider, task, contact);
    } finally {
      scanning.delete(task.id);
    }
  }
};
